import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './TechnicalService.scss';

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const STATUS_META = {
  1: { label: 'Yeni Talep', className: 'new' },
  2: { label: 'İşlem Bekleniyor', className: 'progress' },
  3: { label: 'İşlem Bekleniyor', className: 'progress' },
  4: { label: 'Bütçe Onayında', className: 'warning' },
  5: { label: 'Bütçe Onayında', className: 'warning' },
  6: { label: 'Müdür Onayladı', className: 'success' },
  7: { label: 'Muhasebeye Aktarıldı', className: 'accounting' },
  8: { label: 'İptal', className: 'cancelled' }
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 1 : parsed;
  }
  return 1;
};

const getStatusMeta = (status) => STATUS_META[normalizeStatus(status)] || STATUS_META[1];

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;

  if (Array.isArray(responseData?.messages) && responseData.messages.length > 0) {
    return responseData.messages.join(' ');
  }

  if (responseData?.message) {
    return responseData.message;
  }

  return fallbackMessage;
};

const formatCurrency = (value) => {
  const numericValue = Number(value || 0);
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(numericValue);
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const createManagerPartRow = () => ({
  localId: `manager-part-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  itemName: '',
  quantity: 1,
  unit: 'Adet',
  unitPrice: '',
  note: ''
});

const getUploadUrl = (fileName) => {
  if (!fileName) return '#';
  if (window.location.hostname === 'localhost') {
    return `http://localhost:5058/uploads/${fileName}`;
  }

  const protocol = window.location.protocol;
  const domain = process.env.REACT_APP_API_BASE_URL.replace(/^https?:\/\//, '');
  return `${protocol}//${domain}/uploads/${fileName}`;
};

const TECHNICAL_SERVICE_TEAMS_CACHE_KEY = 'technicalServiceTeamsCacheV1';

const readTeamsCache = () => {
  try {
    const raw = sessionStorage.getItem(TECHNICAL_SERVICE_TEAMS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeTeamsCache = (payload) => {
  try {
    sessionStorage.setItem(TECHNICAL_SERVICE_TEAMS_CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    // ignore cache failures
  }
};

const TechnicalServiceTeamsPage = () => {
  const initialCache = readTeamsCache();
  const [requests, setRequests] = useState(initialCache?.requests || []);
  const [loading, setLoading] = useState(!(initialCache?.requests?.length > 0));
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('budget');
  const [selectedRequestId, setSelectedRequestId] = useState(initialCache?.selectedRequestId || null);
  const [selectedRequestDetail, setSelectedRequestDetail] = useState(initialCache?.selectedRequestDetail || null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [approvalForm, setApprovalForm] = useState({
    managerNote: '',
    usedPartPrices: {},
    managerUsedParts: [createManagerPartRow()]
  });

  const fetchRequests = async () => {
    const response = await api.get('/TechnicalService/requests');
    const items = response?.data?.data || [];
    setRequests(items);
    writeTeamsCache({
      requests: items,
      selectedRequestId,
      selectedRequestDetail
    });
    return items;
  };

  const openRequestDetail = async (requestId, fallbackItem = null) => {
    const listItem = fallbackItem || requests.find((item) => item.id === requestId) || null;

    try {
      setDetailLoading(true);
      setSelectedRequestId(requestId);

      if (listItem) {
        setSelectedRequestDetail(listItem);
      }

      const response = await api.get(`/TechnicalService/requests/${requestId}`);
      const detail = response?.data?.data || null;
      setSelectedRequestDetail(detail);
      setErrorMessage('');
      writeTeamsCache({
        requests,
        selectedRequestId: requestId,
        selectedRequestDetail: detail
      });
      setApprovalForm({
        managerNote: detail?.managerNote || '',
        usedPartPrices: (detail?.workLogs || []).reduce((acc, workLog) => {
          (workLog.usedParts || []).forEach((part) => {
            acc[part.id] = part.unitPrice ?? '';
          });
          return acc;
        }, {}),
        managerUsedParts: [createManagerPartRow()]
      });
    } catch (error) {
      console.error('Talep detayı alınamadı:', error);
      if (!listItem) {
        setSelectedRequestDetail(null);
      }
      setErrorMessage(getApiErrorMessage(error, 'Talep detayı alınamadı.'));
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        if (requests.length === 0) {
          setLoading(true);
        }
        await fetchRequests();
      } catch (error) {
        console.error('Servis durumu verileri alınamadı:', error);
        setErrorMessage(getApiErrorMessage(error, 'Bütçe onayı yüklenemedi.'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    writeTeamsCache({
      requests,
      selectedRequestId,
      selectedRequestDetail
    });
  }, [requests, selectedRequestId, selectedRequestDetail]);

  const requestRows = useMemo(
    () =>
      requests
        .map((request) => ({
          ...request,
          normalizedStatus: normalizeStatus(request.status),
          statusMeta: getStatusMeta(request.status),
          assignedNames: request.assignments?.map((assignment) => assignment.assignedUserName).join(', ') || '-',
          machineLabel: request.towerCraneSerialNo || request.externalMachineName || '-',
          siteLabel: request.workSiteName || request.externalWorkSiteName || '-'
        }))
        .filter((request) => request.normalizedStatus >= 2 && request.normalizedStatus <= 5),
    [requests]
  );

  const waitingRows = useMemo(
    () => requestRows.filter((request) => request.normalizedStatus < 4),
    [requestRows]
  );

  const budgetRows = useMemo(
    () => requestRows.filter((request) => request.normalizedStatus >= 4),
    [requestRows]
  );

  const visibleRows = activeTab === 'budget' ? budgetRows : waitingRows;

  useEffect(() => {
    if (visibleRows.length === 0) {
      setSelectedRequestId(null);
      setSelectedRequestDetail(null);
      return;
    }

    if (!visibleRows.some((request) => request.id === selectedRequestId)) {
      openRequestDetail(visibleRows[0].id, visibleRows[0]);
    }
  }, [activeTab, selectedRequestId, visibleRows]);

  const handleUsedPartPriceChange = (partId, value) => {
    setApprovalForm((prev) => ({
      ...prev,
      usedPartPrices: {
        ...prev.usedPartPrices,
        [partId]: value
      }
    }));
  };

  const selectedRequest =
    visibleRows.find((request) => request.id === selectedRequestId) || visibleRows[0] || null;

  const isBudgetApproval = selectedRequest ? selectedRequest.normalizedStatus >= 4 : false;

  const completedWorkLogs = useMemo(
    () => (selectedRequestDetail?.workLogs || []).filter((workLog) => workLog.isCompleted),
    [selectedRequestDetail]
  );

  const allUsedParts = useMemo(
    () => completedWorkLogs.flatMap((workLog) => workLog.usedParts || []),
    [completedWorkLogs]
  );

  const normalizedManagerUsedParts = useMemo(
    () => (approvalForm.managerUsedParts || []).filter((part) => String(part.itemName || '').trim()),
    [approvalForm.managerUsedParts]
  );

  const calculatedTotal = useMemo(
    () =>
      allUsedParts.reduce((sum, part) => {
        const unitPrice = Number(approvalForm.usedPartPrices[part.id] ?? part.unitPrice ?? 0);
        return sum + Number(part.quantity || 0) * unitPrice;
      }, 0) +
      normalizedManagerUsedParts.reduce((sum, part) => {
        return sum + Number(part.quantity || 0) * Number(part.unitPrice || 0);
      }, 0),
    [allUsedParts, approvalForm.usedPartPrices, normalizedManagerUsedParts]
  );

  const updateManagerPart = (localId, field, value) => {
    setApprovalForm((prev) => ({
      ...prev,
      managerUsedParts: prev.managerUsedParts.map((part) =>
        part.localId === localId ? { ...part, [field]: value } : part
      )
    }));
  };

  const addManagerPart = () => {
    setApprovalForm((prev) => ({
      ...prev,
      managerUsedParts: [...prev.managerUsedParts, createManagerPartRow()]
    }));
  };

  const removeManagerPart = (localId) => {
    setApprovalForm((prev) => ({
      ...prev,
      managerUsedParts:
        prev.managerUsedParts.length === 1
          ? prev.managerUsedParts
          : prev.managerUsedParts.filter((part) => part.localId !== localId)
    }));
  };

  const handleApprove = async () => {
    if (!selectedRequestDetail) return;

    setApprovalSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await api.post('/TechnicalService/requests/approve', {
        requestId: selectedRequestDetail.id,
        totalPrice: calculatedTotal,
        managerNote: approvalForm.managerNote,
        sendToAccounting: true,
        usedPartPrices: allUsedParts.map((part) => ({
          usedPartId: part.id,
          unitPrice: Number(approvalForm.usedPartPrices[part.id] ?? part.unitPrice ?? 0)
        })),
        managerUsedParts: normalizedManagerUsedParts.map((part) => ({
          itemName: part.itemName,
          quantity: Number(part.quantity || 0),
          unit: part.unit,
          unitPrice: Number(part.unitPrice || 0),
          note: part.note || ''
        }))
      });

      setSuccessMessage('Talep onaylandı ve muhasebeye aktarıldı.');
      const items = await fetchRequests();
      const actionable = items
        .map((item) => ({ ...item, normalizedStatus: normalizeStatus(item.status) }))
        .filter((item) => item.normalizedStatus >= 2 && item.normalizedStatus <= 5);

      const nextWaiting = actionable.filter((item) => item.normalizedStatus < 4);
      const nextBudget = actionable.filter((item) => item.normalizedStatus >= 4);
      const nextTab = nextBudget.length > 0 ? 'budget' : 'waiting';
      const nextRows = nextTab === 'budget' ? nextBudget : nextWaiting;

      setActiveTab(nextTab);

      if (nextRows.some((item) => item.id === selectedRequestDetail.id)) {
        await openRequestDetail(selectedRequestDetail.id);
      } else if (nextRows.length > 0) {
        await openRequestDetail(nextRows[0].id, nextRows[0]);
      } else {
        setSelectedRequestId(null);
        setSelectedRequestDetail(null);
      }
    } catch (error) {
      console.error('Bütçe onayı yapılamadı:', error);
      setErrorMessage(getApiErrorMessage(error, 'Bütçe onayı yapılamadı.'));
    } finally {
      setApprovalSubmitting(false);
    }
  };

  return (
    <div className="technical-service-page">
      <Navbar />
      <div className="technical-service-container">
        <div className="technical-service-header">
          <div>
            <h1>Bütçe Onayı</h1>
          </div>
        </div>

        {errorMessage && <div className="technical-service-alert error">{errorMessage}</div>}
        {successMessage && <div className="technical-service-alert success">{successMessage}</div>}

        <div className="technical-service-grid manager-layout budget-approval-layout">
          <section className="technical-service-card">
            <div className="panel-tabs">
              <button
                type="button"
                className={activeTab === 'budget' ? 'tab-btn active' : 'tab-btn'}
                onClick={() => setActiveTab('budget')}
              >
                Bütçe Onayında
                <span>{budgetRows.length}</span>
              </button>
              <button
                type="button"
                className={activeTab === 'waiting' ? 'tab-btn active' : 'tab-btn'}
                onClick={() => setActiveTab('waiting')}
              >
                İşlem Bekleyenler
                <span>{waitingRows.length}</span>
              </button>
            </div>

            {loading && visibleRows.length === 0 ? (
              <p>Bütçe onayı yükleniyor...</p>
            ) : visibleRows.length > 0 ? (
              <div className="request-stack compact-request-stack">
                {visibleRows.map((request) => (
                  <button
                    key={request.id}
                    type="button"
                    className={`request-panel request-button compact-request-card ${selectedRequest?.id === request.id ? 'active' : ''}`}
                    onClick={() => openRequestDetail(request.id, request)}
                  >
                    <div className="compact-request-top">
                      <div className="compact-request-main">
                        <strong>{request.requestNo || `Talep #${request.id}`}</strong>
                        <p>{request.requestingCompanyName}</p>
                        <span>{request.subject}</span>
                      </div>
                      <span className={`badge ${request.statusMeta.className}`}>
                        {request.statusMeta.label}
                      </span>
                    </div>

                    <div className="compact-request-meta">
                      <div><strong>Makine:</strong> {request.machineLabel}</div>
                      <div><strong>Şantiye:</strong> {request.siteLabel}</div>
                      <div><strong>Ekip:</strong> {request.assignedNames}</div>
                      <div><strong>Konum:</strong> {request.workSiteLocation || '-'}</div>
                      <div><strong>Operasyon:</strong> {formatDate(request.operationDate)}</div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-form-state compact centered-empty-state">
                <p>{activeTab === 'budget' ? 'Bütçe onayında talep bulunmuyor.' : 'İşlem bekleyen talep bulunmuyor.'}</p>
              </div>
            )}
          </section>

          <section className="technical-service-card budget-detail-card">
            <h2>{isBudgetApproval ? 'Bütçe Onayı Detayı' : 'Talep Detayı'}</h2>
            {detailLoading ? (
              <p>Talep detayı yükleniyor...</p>
            ) : selectedRequestDetail ? (
              isBudgetApproval ? (
                <div className="approval-panel">
                  <div className="job-summary compact">
                    <div><strong>Talep No:</strong> {selectedRequestDetail.requestNo}</div>
                    <div><strong>Firma:</strong> {selectedRequestDetail.requestingCompanyName}</div>
                    <div><strong>Makine:</strong> {selectedRequestDetail.towerCraneSerialNo || selectedRequestDetail.externalMachineName || '-'}</div>
                    <div><strong>Konum:</strong> {selectedRequestDetail.workSiteLocation || '-'}</div>
                    <div><strong>Operasyon:</strong> {formatDate(selectedRequestDetail.operationDate)}</div>
                    <div><strong>Şantiye:</strong> {selectedRequestDetail.workSiteName || selectedRequestDetail.externalWorkSiteName || '-'}</div>
                    <div><strong>Durum:</strong> Bütçe Onayında</div>
                    <div><strong>Talep Tarihi:</strong> {formatDate(selectedRequestDetail.requestedDate)}</div>
                  </div>

                  <div className="approval-worklogs">
                    {completedWorkLogs.map((workLog) => (
                      <div key={workLog.id} className="approval-worklog-card">
                        <div className="approval-worklog-header">
                          <strong>{workLog.userName}</strong>
                          <span>{formatDate(workLog.processDate)}</span>
                        </div>
                        <p>{workLog.description || 'Aciklama girilmedi.'}</p>
                        {workLog.files?.length > 0 && (
                          <div className="file-pill-row">
                            {workLog.files.map((file) => (
                              <a
                                key={file.id}
                                className="file-pill"
                                href={getUploadUrl(file.fileName)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {file.fileName}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="parts-section manager-parts-section">
                    <div className="parts-header">
                      <h3>Fatura Kalemleri</h3>
                    </div>

                    {allUsedParts.length > 0 ? (
                      allUsedParts.map((part) => (
                        <div key={part.id} className="parts-row manager-parts-row">
                          <div className="part-readonly-box">
                            <strong>{part.itemName}</strong>
                            <span>{Number(part.quantity || 0)} {part.unit || ''}</span>
                          </div>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Birim Fiyat"
                            value={approvalForm.usedPartPrices[part.id] ?? ''}
                            onChange={(event) => handleUsedPartPriceChange(part.id, event.target.value)}
                          />
                          <div className="part-total-box">
                            {formatCurrency(Number(part.quantity || 0) * Number(approvalForm.usedPartPrices[part.id] ?? 0))}
                          </div>
                        </div>
                      ))
                    ) : (
                      null
                    )}

                    <div className="parts-header manager-extra-parts-header">
                      <button type="button" className="secondary-btn" onClick={addManagerPart}>
                        Kalem Ekle
                      </button>
                    </div>

                    {approvalForm.managerUsedParts.map((part) => (
                      <div key={part.localId} className="parts-row manager-extra-parts-row">
                        <input
                          placeholder="Kalem Adı"
                          value={part.itemName}
                          onChange={(event) => updateManagerPart(part.localId, 'itemName', event.target.value)}
                        />
                        <input
                          type="number"
                          min="1"
                          step="1"
                          placeholder="Miktar"
                          value={part.quantity}
                          onChange={(event) => updateManagerPart(part.localId, 'quantity', event.target.value)}
                        />
                        <input
                          placeholder="Birim"
                          value={part.unit}
                          onChange={(event) => updateManagerPart(part.localId, 'unit', event.target.value)}
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Birim Fiyat"
                          value={part.unitPrice}
                          onChange={(event) => updateManagerPart(part.localId, 'unitPrice', event.target.value)}
                        />
                        <button
                          type="button"
                          className="secondary-btn danger"
                          onClick={() => removeManagerPart(part.localId)}
                          disabled={approvalForm.managerUsedParts.length === 1}
                        >
                          Sil
                        </button>
                      </div>
                    ))}

                    <div className="parts-header manager-total-footer">
                      <strong>Toplam Tutar</strong>
                      <strong>{formatCurrency(calculatedTotal)}</strong>
                    </div>
                  </div>

                  <label className="manager-note-label">
                    Müdür Notu
                    <textarea
                      rows="4"
                      value={approvalForm.managerNote}
                      onChange={(event) =>
                        setApprovalForm((prev) => ({
                          ...prev,
                          managerNote: event.target.value
                        }))
                      }
                      placeholder="Muhasebe için not"
                    />
                  </label>

                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleApprove}
                    disabled={approvalSubmitting}
                  >
                    {approvalSubmitting ? 'Onaylanıyor...' : 'Onayla ve Muhasebeye Gönder'}
                  </button>
                </div>
              ) : (
                <div className="status-detail-panel">
                  <div className="job-summary compact">
                    <div><strong>Talep No:</strong> {selectedRequestDetail.requestNo}</div>
                    <div><strong>Firma:</strong> {selectedRequestDetail.requestingCompanyName}</div>
                    <div><strong>Makine:</strong> {selectedRequestDetail.towerCraneSerialNo || selectedRequestDetail.externalMachineName || '-'}</div>
                    <div><strong>Şantiye:</strong> {selectedRequestDetail.workSiteName || selectedRequestDetail.externalWorkSiteName || '-'}</div>
                    <div><strong>Konum:</strong> {selectedRequestDetail.workSiteLocation || '-'}</div>
                    <div><strong>Operasyon:</strong> {formatDate(selectedRequestDetail.operationDate)}</div>
                    <div><strong>Atanan Personeller:</strong> {selectedRequest?.assignedNames || '-'}</div>
                    <div><strong>Durum:</strong> İşlem Bekleniyor</div>
                  </div>

                  <div className="empty-form-state compact status-note-card">
                    <h3>Süreç sahada devam ediyor</h3>
                  </div>
                </div>
              )
            ) : (
              <div className="empty-form-state compact">
                <h3>Talep seçimi bekleniyor</h3>
                <p>Soldan bir kayıt seç. İşlem bekleyen kayıtları takip edebilir, tamamlanan kayıtları burada bütçe onayına alabilirsin.</p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TechnicalServiceTeamsPage;

